// @ts-check
// touch-ux.js — Touch-only behaviours grouped into one place:
//   • Pull-to-refresh on the mobile file list
//   • iOS soft-keyboard avoidance (scrolls focused input into view)
//
// Both blocks are no-ops on non-touch devices (gated on
// `window.matchMedia('(pointer: coarse)')`).

import { NoteEditor } from '../editor/note-editor.js';
import { updateFileList } from '../notes/file-list.js';
import { getSyncHelpers } from '../storage/session.js';
import { updateStatus } from './status-pill.js';
import { debounce } from '../utils/debounce.js';
import { coarsePointerQuery } from '../utils/coarse-pointer.js';

// ── Pull-to-refresh on the mobile file list ─────────────────────────────
// On touch devices, pulling the file list down past its top edge triggers
// a sync refresh (when sync is on) and a fresh file-list rebuild. The
// indicator is a small themed strip that appears at the top of the list.
// Desktop browsers ignore the gesture: scroll-up at top is the platform
// idiom for chrome reveal, not refresh.
(function setupPullToRefresh() {
  if (!coarsePointerQuery.matches) return;

  const list = document.getElementById('fileList') as any;
  const filesContainer = document.getElementById('files-container');
  if (!list || !filesContainer) return;

  // Threshold pulls feel snappy at ~64px on iOS. Below that, treat as scroll.
  const PULL_THRESHOLD = 64;
  const MAX_PULL = 100;
  // Soft resistance curve: actual pull distance is multiplied by this so
  // long pulls feel rubbery near the cap. Tunable in one place; previously
  // hardcoded inline.
  const PTR_RESISTANCE = 0.55;

  /** @type {any} */
  let startY: any = null;
  let armed = false;
  let pull = 0;
  let refreshing = false;

  // Indicator strip lives just inside the files container above the list so
  // it slides in naturally as the list translates downward.
  const indicator = document.createElement('div');
  indicator.className = 'pull-to-refresh-indicator';
  indicator.innerHTML =
    '<span class="ptr-arrow">⌄</span>' + '<span class="ptr-label">Pull to refresh</span>';
  filesContainer.insertBefore(indicator, list);
  // The indicator (and its .ptr-label child) is built once and never replaced,
  // so hoist the lookup out of the per-frame touchmove path (setPull) instead
  // of re-querying it on every move.
  const ptrLabel = indicator.querySelector('.ptr-label');

  /**
   * @param {number} px
   * @returns {void}
   */
  function setPull(px: any) {
    pull = Math.max(0, Math.min(MAX_PULL, px));
    list.style.transform = pull > 0 ? `translateY(${pull}px)` : '';
    indicator.style.opacity = String(Math.min(1, pull / PULL_THRESHOLD));
    indicator.style.transform = `translateY(${Math.min(pull, PULL_THRESHOLD) - PULL_THRESHOLD}px)`;
    indicator.classList.toggle('ptr-armed', pull >= PULL_THRESHOLD);
    if (ptrLabel) {
      ptrLabel.textContent =
        pull >= PULL_THRESHOLD
          ? getSyncHelpers()?.enabled
            ? 'Release to sync'
            : 'Release to refresh'
          : getSyncHelpers()?.enabled
            ? 'Pull to sync'
            : 'Pull to refresh';
    }
  }

  /**
   * @param {boolean} [animated]
   * @returns {void}
   */
  function reset(animated = true) {
    if (animated) {
      list.style.transition = 'transform 0.25s var(--easing-bounce)';
      indicator.style.transition = 'opacity 0.2s ease, transform 0.25s var(--easing-bounce)';
    }
    pull = 0;
    list.style.transform = '';
    indicator.style.opacity = '0';
    indicator.style.transform = `translateY(-${PULL_THRESHOLD}px)`;
    indicator.classList.remove('ptr-armed');
    setTimeout(() => {
      list.style.transition = '';
      indicator.style.transition = '';
    }, 280);
  }

  list.addEventListener(
    'touchstart',
    (e: any) => {
      if (refreshing) return;
      if (list.scrollTop > 0) {
        armed = false;
        return;
      }
      startY = e.touches[0].clientY;
      armed = true;
    },
    { passive: true }
  );

  list.addEventListener(
    'touchmove',
    (e: any) => {
      if (!armed || startY == null) return;
      const dy = e.touches[0].clientY - startY;
      if (dy <= 0) {
        setPull(0);
        return;
      }
      // Apply a soft resistance curve so the pull feels rubbery near the cap.
      setPull(dy * PTR_RESISTANCE);
    },
    { passive: true }
  );

  list.addEventListener('touchend', async () => {
    if (!armed) return;
    armed = false;
    startY = null;
    if (pull < PULL_THRESHOLD || refreshing) {
      reset();
      return;
    }

    refreshing = true;
    indicator.classList.add('ptr-refreshing');
    if (ptrLabel) ptrLabel.textContent = 'Syncing…';

    try {
      if (getSyncHelpers()?.enabled && typeof getSyncHelpers()?.refresh === 'function') {
        await getSyncHelpers().refresh();
      }
      await updateFileList();
      updateStatus(getSyncHelpers()?.enabled ? 'Synced.' : 'Refreshed.', {
        kind: 'success',
        duration: 1600,
      });
    } catch (e: any) {
      updateStatus(`Refresh failed: ${e?.message || e}`, { kind: 'error' });
    } finally {
      refreshing = false;
      indicator.classList.remove('ptr-refreshing');
      reset();
    }
  });

  list.addEventListener('touchcancel', () => {
    armed = false;
    startY = null;
    reset();
  });
})();

// ── iOS / soft-keyboard avoidance ───────────────────────────────────────
// When a non-editor input gains focus on a touch device, the soft keyboard
// can occlude it. Schedule a scroll-into-view once the keyboard finishes
// animating in. Skips the main editor textarea (it manages its own scroll
// position based on caret line) and skips desktop where there's no keyboard.
// Backed by visualViewport.resize so a late keyboard show still scrolls the
// active input into the visible area.
(function setupKeyboardAvoidance() {
  if (!coarsePointerQuery.matches) return;

  /**
   * @param {any} el - the focused element (or an EventTarget)
   * @returns {boolean}
   */
  function _shouldScroll(el: any) {
    if (!el) return false;
    if (el === NoteEditor.contentDOM()) return false; // editor manages itself
    if (el.id === 'searchBox' || el.id === 'searchTasksBox') return false;
    const tag = (el.tagName || '').toUpperCase();
    return tag === 'INPUT' || tag === 'TEXTAREA';
  }

  /** @returns {void} */
  function _scrollActiveIntoView() {
    const el = document.activeElement as any;
    if (!_shouldScroll(el)) return;
    // Use scrollIntoView with block:center so the input lands above the
    // keyboard with breathing room. Smooth behaviour matches the bouncy
    // ethos elsewhere in the app.
    try {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    } catch (_) {
      el.scrollIntoView();
    }
  }

  document.addEventListener('focusin', (e) => {
    if (!_shouldScroll(e.target)) return;
    // 300ms is enough to clear iOS' keyboard slide-in animation on modern
    // hardware. A second nudge after 600ms catches the case where iOS
    // takes longer (cold start, backgrounded apps).
    setTimeout(_scrollActiveIntoView, 300);
    setTimeout(_scrollActiveIntoView, 600);
  });

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', debounce(_scrollActiveIntoView, 80));
  }
})();
